import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid, TextField } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AccessTime } from '@mui/icons-material';

import Form from 'components/Form';
import MuiDialog from './components/MuiDialog';
import useNotification from 'hooks/useNotification';

import { IPathology } from '../PatientList/api/type';

interface Props {
  isUpdate: boolean;
  defaultValue: Partial<IPathology>;
  onClose: () => void;
  onSave: (data: IPathology) => void;
  onSearchNo: (no: number) => boolean;
}

const PathologyDialog = (props: Props) => {
  const { isUpdate, defaultValue, onClose, onSave, onSearchNo } = props;
  const [isSearched, setIsSearched] = useState(isUpdate);
  const [pathologyTime, setPathologyTime] = useState<Date | null>(
    new Date(defaultValue.pathologyTime!)
  );

  const { onSuccess, onRequired } = useNotification();
  const { handleSubmit, register, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const onSubmit = (data: any) => {
    if (!isUpdate && onSearchNo(Number(data.ptPathologyNo)))
      return onRequired('REQUIRED.UNIQUE.NO');
    if (!isSearched) return onRequired('REQUIRED.FEE.ID');
    if (pathologyTime?.toString() === 'Invalid Date')
      return onRequired('REQUIRED.DATE.TIME');

    onSuccess('저장되었습니다');
    onSave({ ...data, pathologyTime });
    onClose();
  };

  const onChangeFeeId = (e: any) => {
    const { value } = e.target;
    setValue('feeId', value);
    setValue('feeKor', '');
    setValue('feeEng', '');
    setIsSearched(false);
  };

  return (
    <MuiDialog.SaveForm
      isOpen
      maxWidth="md"
      title="임상병리검사 데이터"
      onClose={onClose}
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Grid container gap={2} sx={{ my: 2 }}>
        <Form.Item required label={'검사번호'}>
          <TextField
            required
            size="small"
            type="number"
            {...register('ptPathologyNo')}
            InputProps={{ readOnly: isUpdate }}
          />
        </Form.Item>
        <Form.Item required label={'검사시간'}>
          <MobileTimePicker
            value={pathologyTime}
            onChange={setPathologyTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Grid item xs={12} />

        <Form.Item required label={'검사코드'}>
          <TextField
            size="small"
            {...register('feeId')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'검사명(한)'}>
          <TextField
            size="small"
            {...register('feeKor')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'검사명(영)'}>
          <TextField
            size="small"
            {...register('feeEng')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'검사결과'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('pathologyResult')}
            />
          </Form.Item>
        </Grid>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'검사범위'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('pathologyRange')}
            />
          </Form.Item>
        </Grid>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'검사노트'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('pathologyNote')}
            />
          </Form.Item>
        </Grid>
      </Grid>
    </MuiDialog.SaveForm>
  );
};

export default PathologyDialog;
